import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";

import { COMPANY_SIZE_MAP } from "../constants";
import { FormFields } from "../types";
import { getOnboardingDestinationUrl, mapDataToFormFields } from "../utils";

import { API_URL, ENDPOINTS } from "@/api/application-api";
import { TOUR_QUERY_PARAM } from "@/components/Playground/tour/hooks/useTourUrlParam";
import { AUTH_CONFIG } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { useOnboardingFlow } from "@/context/onboarding-flow-context";
import { useWorkspaces } from "@/queries";
import {
  USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS,
  useUserOnboarding,
  useUserOnboardingQuestion,
} from "@/queries/onboarding";
import { MutateOnboardingData } from "@/queries/onboarding/types";
import { useCreateOrganizationWithWorkspaceAndInvites } from "@/queries/organizations/useCreateOrganizationWithWorkspaceAndInvites";
import {
  getInitialActiveWorkspaceId,
  getUserDefaultWorkspaceId,
  resolveActiveWorkspaceId,
} from "@/utils/activeWorkspace";
import { hasPendingRemix, readAndClearPendingRemix } from "@/utils/remix-cookie";
import { displayErrorToast } from "@/utils/toast";

const tryAutoRemix = async (workspaceId: number, userToken: string): Promise<number | null> => {
  const pendingRemix = readAndClearPendingRemix();
  if (!pendingRemix) {
    return null;
  }

  try {
    const configResponse = await fetch(`${API_URL}/api/sessions/${pendingRemix.share_token}`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${userToken}` },
    });
    const config = await configResponse.json();

    if (!config.success || !config.session) {
      return null;
    }

    const remixResponse = await fetch(`${API_URL}/api/sessions/${pendingRemix.share_token}/remix`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      credentials: "include",
      body: JSON.stringify({
        workspace_id: workspaceId,
        name: config.session.name,
        folder_id: null,
      }),
    });
    const remix = await remixResponse.json();

    return remix.success ? (remix.prompt_template.id as number) : null;
  } catch {
    return null;
  }
};

export const useOnboardingSetup = () => {
  const { isOnPrem, companyName } = AUTH_CONFIG;
  const auth = useAuth();
  const authUserRef = useRef(auth?.user);
  authUserRef.current = auth?.user;
  const userToken = auth?.userToken ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    onboardingDestination,
    setOnboardingDestination,
    setOnboardingPath,
    clearOnboardingFlow,
  } = useOnboardingFlow();
  const [isSubmittingFlow, setIsSubmittingFlow] = useState(false);
  const { data: existingWorkspaces, refetch: refetchWorkspaces } = useWorkspaces();

  const methods = useForm<FormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: onboardingDestination?.formValues ?? {
      you: { role: "prompt_engineer", specific_role: "" },
      company: { company_name: companyName ?? "", company_size: "" },
      product_areas: ["prompts_and_evals"],
      invitee_emails: [],
    },
  });

  const { mutateAsync: submitOnboarding, isLoading: isSubmittingOnboarding } =
    useUserOnboarding(userToken);
  const { mutateAsync: createOrganization, isLoading: isCreatingOrganization } =
    useCreateOrganizationWithWorkspaceAndInvites();
  const { data: answers, isLoading: isLoadingAnswers } = useUserOnboardingQuestion(userToken, {
    enabled: !isOnPrem,
  });

  useEffect(() => {
    if (isOnPrem || !answers?.onboarding_responses.length) {
      return;
    }

    const values = mapDataToFormFields(answers.onboarding_responses);
    if (values) {
      methods.reset(onboardingDestination?.formValues ?? values);
    }
  }, [answers, isOnPrem, methods, onboardingDestination?.formValues]);

  const navigateToWorkspace = async (
    organizationId: number,
    defaultWorkspaceId?: number,
    defaultPromptTemplateId?: number,
  ) => {
    const { data } = await refetchWorkspaces();
    const organizationWorkspace = data?.workspaces.find(
      workspace => workspace.organization_id === organizationId && workspace.is_default,
    );
    const workspaceId =
      defaultWorkspaceId ??
      organizationWorkspace?.id ??
      resolveActiveWorkspaceId({
        workspaces: data?.workspaces ?? [],
        storedWorkspaceId: getInitialActiveWorkspaceId(),
        defaultWorkspaceId: getUserDefaultWorkspaceId(authUserRef.current),
      });

    if (!workspaceId) {
      return;
    }

    if (defaultPromptTemplateId) {
      const destinationUrl = await getOnboardingDestinationUrl({
        workspaceId,
        promptTemplateId: defaultPromptTemplateId,
        userToken,
      });
      if (!destinationUrl) {
        displayErrorToast("We couldn't open your starter prompt. Please try again.");
        return;
      }

      window.location.replace(destinationUrl);
      return;
    }

    navigate(`/workspace/${workspaceId}/playground?${TOUR_QUERY_PARAM}`, { replace: true });
  };

  const submitSetup = async (data: FormFields) => {
    const role = data.you.role === "other" ? data.you.specific_role.trim() : data.you.role;
    const organizationName = isOnPrem
      ? companyName || data.company.company_name
      : data.company.company_name;

    if (!isOnPrem) {
      const response: MutateOnboardingData = {
        about_user: {
          person_name: authUserRef.current?.name || "",
          role,
          company_size: data.company.company_size,
          company_name: organizationName,
        },
      };

      await submitOnboarding({
        onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.ABOUT_USER,
        response,
      });
    }

    const existingWorkspace =
      existingWorkspaces?.workspaces.find(workspace => workspace.is_default) ??
      existingWorkspaces?.workspaces[0];
    const createdOrganization = existingWorkspace
      ? null
      : await createOrganization({
          organization_name: organizationName || "My Organization",
          workspace_name: "Default",
          invitee_emails: data.invitee_emails,
        });
    const organizationId = createdOrganization?.id ?? existingWorkspace?.organization_id;

    if (!organizationId) {
      return;
    }

    const { data: workspacesData } = await refetchWorkspaces();
    const workspace =
      workspacesData?.workspaces.find(
        candidate => candidate.organization_id === organizationId && candidate.is_default,
      ) ??
      workspacesData?.workspaces.find(candidate => candidate.organization_id === organizationId);

    if (!isOnPrem && workspace && hasPendingRemix()) {
      const remixedPromptId = await tryAutoRemix(workspace.id, userToken);
      if (remixedPromptId !== null) {
        await submitOnboarding({
          onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE,
          response: { use_case: "remix" },
        });
        posthog.people.set({ "Onboarding Completed": true });
        navigate(`/workspace/${workspace.id}/prompt/${remixedPromptId}`);
        return;
      }
    }

    posthog.capture(isOnPrem ? "Onboarding Completed" : "Onboarding Profile Completed", {
      role,
      company_size: data.company.company_size,
    });
    posthog.people.set({
      ...(isOnPrem ? { "Onboarding Completed": true } : {}),
      "Company Size": COMPANY_SIZE_MAP[data.company.company_size],
      "Company Name": organizationName,
      Role: role,
    });

    if (isOnPrem) {
      await navigateToWorkspace(
        organizationId,
        createdOrganization?.default_workspace_id,
        createdOrganization?.default_prompt_template_id,
      );
      return;
    }

    const onboardingWorkspaceId = createdOrganization?.default_workspace_id ?? workspace?.id;
    const onboardingUserId = authUserRef.current?.id;
    if (!onboardingWorkspaceId || !onboardingUserId) {
      return;
    }

    const promptTemplateId = createdOrganization?.default_prompt_template_id ?? null;

    if (data.product_areas.includes("observability")) {
      setOnboardingDestination({
        userId: String(onboardingUserId),
        workspaceId: onboardingWorkspaceId,
        promptTemplateId,
        formValues: data,
      });
      setOnboardingPath("observability");
      posthog.capture("Onboarding Observability Selected");
      await queryClient.invalidateQueries({ queryKey: [ENDPOINTS.user_onboarding] });
      return;
    }

    const destinationUrl = await getOnboardingDestinationUrl({
      workspaceId: onboardingWorkspaceId,
      promptTemplateId,
      userToken,
    });
    if (!destinationUrl) {
      displayErrorToast("We couldn't open your starter prompt. Please try again.");
      return;
    }

    const response = await submitOnboarding({
      onboarding_question_key: USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE,
      response: { use_case: "prompts_and_evals" },
    });
    if (!response.success) {
      return;
    }

    posthog.capture("Onboarding Prompts and Evals Selected");
    posthog.people.set({ "Onboarding Completed": true });
    clearOnboardingFlow();
    window.location.replace(destinationUrl);
  };

  const onSubmit = async (data: FormFields) => {
    setIsSubmittingFlow(true);
    try {
      await submitSetup(data);
    } finally {
      setIsSubmittingFlow(false);
    }
  };

  return {
    methods,
    onSubmit,
    firstName: auth?.user?.name?.trim().split(/\s+/)[0] ?? "",
    isLoadingAnswers: isOnPrem ? false : isLoadingAnswers,
    isSubmitting: isSubmittingFlow || isSubmittingOnboarding || isCreatingOrganization,
    hasExistingWorkspace: Boolean(existingWorkspaces?.workspaces.length),
  };
};
